import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatFinance } from "../../../../utils/formatters";
import { getChannelDailySales } from "../../../../api/report/orderStatsReport";

// Define colors for different channels
const CHANNEL_COLORS = {
  indoor: "#8884d8",
  ddireto: "#b6722a",
  neemo: "#ffc658",
  ifood: "#ff7c7c",
  slapie_pos: "#8dd1e1",
};

// Fallback colors for channels not defined above
const FALLBACK_COLORS = [
  "#d084d0",
  "#84d084",
  "#d0d084",
  "#d08484",
  "#84d0d0",
  "#9984d0",
  "#d09984",
  "#84d099",
];

export const ChannelDailySalesChart = () => {
  const [data, setData] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const { data } = await getChannelDailySales();

      console.log("DATAAAAA", data);

      setData(data.chart_data);
      setChannels(data.channels);
      setDateRange(data.date_range);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getChannelColor = (channel, index) => {
    return (
      CHANNEL_COLORS[channel] || FALLBACK_COLORS[index % FALLBACK_COLORS.length]
    );
  };

  const formatChannelName = (channel) => {
    const channelNames = {
      indoor: "Salão",
      delivery: "Delivery",
      takeout: "Retirada",
      pos: "POS",
      pickup: "Pickup",
    };
    return (
      channelNames[channel] ||
      channel.charAt(0).toUpperCase() + channel.slice(1)
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Erro ao carregar dados de vendas: {error}
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Vendas por Canal (Últimos 30 Dias)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Comparação diária de vendas entre todos os canais de venda
        {dateRange && (
          <span>
            {" "}
            ({new Date(dateRange.start_date).toLocaleDateString("pt-BR")} -{" "}
            {new Date(dateRange.end_date).toLocaleDateString("pt-BR")})
          </span>
        )}
      </Typography>

      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => {
                const d = new Date(date);
                return `${d.getDate()}/${d.getMonth() + 1}`;
              }}
            />
            <YAxis tickFormatter={(value) => formatFinance(value)} />
            <Tooltip
              formatter={(value, name) => [
                formatFinance(value),
                formatChannelName(name),
              ]}
              labelFormatter={(date) => {
                const d = new Date(date);
                return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
              }}
            />
            <Legend formatter={formatChannelName} />

            {channels.map((channel, index) => (
              <Line
                key={channel}
                type="monotone"
                dataKey={channel}
                stroke={getChannelColor(channel, index)}
                activeDot={{ r: 6 }}
                name={channel}
                connectNulls={false}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};
